import React, { useCallback, useContext, useState } from "react";
import requestCache from "../../../utils/requestCache";
import { mutateErrors } from "../../../utils";
import { useUserId } from "../../user/hooks/useUserId";
import { NotifyMode } from "../../notify/NotifyModeEnum";
import { useNotify } from "../../notify/useNotify";
import { parseToManagedStudentsFrontEndModel } from "../helpers/parseToManagedStudentsFrontEndModel";
import useStudentsQueries from "../hooks/useManagedStudentsQueries";
import { getFilterOptionsForStudents } from "../helpers/helperFunctions";
import { IManagedStudentsFrontEndModel } from "../types";
import { UserRole } from "../../user/enums/UserRole";

const ManagedStudentsPageContext = React.createContext<
  Partial<ReturnType<typeof useManagedStudentsPageProvider>>
>({});

export const ManagedStudentsPageProvider: React.FC<{
  userRole: UserRole.Agent | UserRole.Parent;
}> = ({ userRole, children }) => {
  const provider = useManagedStudentsPageProvider(userRole);

  return (
    <ManagedStudentsPageContext.Provider value={provider}>
      {children}
    </ManagedStudentsPageContext.Provider>
  );
};

export const useManagedStudentsPage = function () {
  return useContext(ManagedStudentsPageContext);
};

const useManagedStudentsPageProvider = function (
  userRole: UserRole.Agent | UserRole.Parent,
) {
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState<IManagedStudentsFrontEndModel[]>([]);
  const { fetchAll } = useStudentsQueries();
  const snackNotify = useNotify();
  const managerId = useUserId();

  const getStudents = useCallback(async (data?) => {
    setLoading(true);

    const filterOptions = getFilterOptionsForStudents(data);

    try {
      const { data } = await requestCache<{
        data: IManagedStudentsFrontEndModel[];
      }>(JSON.stringify(filterOptions) + managerId, () =>
        fetchAll(filterOptions, managerId, userRole).then((response) =>
          parseToManagedStudentsFrontEndModel(response),
        ),
      );
      setStudents(data);
      setLoading(false);
    } catch (e) {
      const { errors, message } = mutateErrors(e);
      snackNotify("Error", message, NotifyMode.ERROR);
      setLoading(false);
      return { ...errors };
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    getStudents,
    students,
    loading,
    userRole,
  };
};
